import DataObjectIcon from "@mui/icons-material/DataObject"
import RefreshIcon from "@mui/icons-material/Refresh"
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard"
import {
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import type { SstApiVersion } from "./saLockerDoorInspection"
import SaLockerProvenanceLine from "./SaLockerProvenanceLine"
import { apiVersionLabel, type ProvenanceDescriptor } from "./saLockerProvenance"

/**
 * The Unit section's header: identity of the INTEGRATION, then occupancy.
 *
 * ## The version chip is the point of this file existing
 *
 * Before LOCKER-355 the only place the SST API version appeared was a chip
 * inside a door drawer — so establishing which integration a locker was on
 * required clicking a door. That is backwards: the version changes what every
 * number below it means, so it belongs above them, always visible, with no
 * interaction.
 *
 * A locker that holds reservations recorded against the OTHER version gets a
 * second chip. A mixed locker rendering as though it were uniform is how a
 * per-door mis-pair gets read as "that door is empty" — the mixedness has to be
 * legible from the section, not inferred by opening doors one at a time.
 *
 * Split out of `SaLockerDashboardView` for size (LOCKER-355 §9, and the
 * repo-wide ~400-line component cap), not because the header is independently
 * meaningful.
 */
export const SaLockerUnitHeader: FC<{
  apiVersion: SstApiVersion
  hasSnapshot: boolean
  isOnline: boolean
  totalDoors: number
  occupiedDoors: number
  /** Count of SL2 rows recorded against the other partner version. */
  versionMismatchedCount: number
  /** Count of SL2 rows that name no door in the snapshot at all. */
  unpairableCount: number
  provenance: ProvenanceDescriptor[]
  onRefresh: () => void
  isRefreshing?: boolean
  onOpenLockerObject: () => void
}> = ({
  apiVersion,
  hasSnapshot,
  isOnline,
  totalDoors,
  occupiedDoors,
  versionMismatchedCount,
  unpairableCount,
  provenance,
  onRefresh,
  isRefreshing,
  onOpenLockerObject,
}) => {
  const { t } = useTranslation()
  const occupancy = totalDoors > 0 ? occupiedDoors / totalDoors : 0

  return (
    <Stack direction="column" spacing={2} sx={{ p: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <SpaceDashboardIcon sx={{ color: "secondary.main" }} />
          {/* The operator dashboard prints the locker name here because it
              stands alone. On this page the header directly above already
              owns identity, so repeating it is noise — the module shells
              below are what needed to be reproduced faithfully. */}
          <Typography variant="subtitle2" color="text.secondary" noWrap>
            {t("saLockerConsole.physicalLayout")}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={apiVersionLabel(apiVersion)}
            data-testid="unit-api-version"
          />
          {versionMismatchedCount > 0 && (
            <Tooltip title={t("saLockerConsole.mixedVersionsHint")}>
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                data-testid="unit-mixed-versions"
                label={t("saLockerConsole.mixedVersions", { n: versionMismatchedCount })}
              />
            </Tooltip>
          )}
          <Tooltip title={t("refresh")}>
            <span>
              <IconButton onClick={onRefresh} disabled={isRefreshing} size="small">
                {isRefreshing ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("saLockerConsole.lockerObject")}>
            <span>
              <IconButton
                onClick={onOpenLockerObject}
                size="small"
                data-testid="open-locker-object"
                aria-label={t("saLockerConsole.lockerObject")}
              >
                <DataObjectIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
          {hasSnapshot && (
            <Typography variant="body2" color={isOnline ? "green" : "error"}>
              {t(isOnline ? "online" : "offline")}
            </Typography>
          )}
          <Typography variant="body2">{`${totalDoors} ${t("doors")}`}</Typography>
        </Stack>
      </Stack>

      {unpairableCount > 0 && (
        <Typography variant="caption" color="warning.main" data-testid="unit-unpairable">
          {t("saLockerConsole.unpairableReservations", { n: unpairableCount })}
        </Typography>
      )}

      <Stack direction="column" spacing={0.25}>
        {provenance.map((d) => (
          <SaLockerProvenanceLine key={`${d.apiVersion}:${d.labelKey}`} descriptor={d} />
        ))}
      </Stack>

      {totalDoors > 0 && (
        <Stack direction="column">
          <Stack direction="row" spacing={1} alignItems="center" color="grey.500">
            <Typography variant="body2">{t("occupancy")}</Typography>
            <Typography variant="body2" data-testid="unit-occupancy">{`${Math.round(
              occupancy * 100,
            )} %`}</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={occupancy * 100}
            sx={{
              width: "100%",
              backgroundColor: "lightgrey",
              "& .MuiLinearProgress-bar": {
                backgroundColor:
                  occupancy > 0.75 ? "red" : occupancy > 0.5 ? "orange" : "green",
              },
            }}
          />
        </Stack>
      )}
    </Stack>
  )
}

export default SaLockerUnitHeader
